import { getYYYYMMDDDate } from '@/logic/helpers/dates/getMeetingDateAndTime';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import hoursOfAssessment from '@/logic/helpers/dates/hoursOfAssessment';
import { takenSlot } from '@/logic/helpers/bookingSlots/helpToApplyTakenSlots';

const applyData = {
  calRef: undefined,

  applyAssessmentsToCalendar(self: Record<string, any>, assessmentsArray: any[]): void {
    const groupedAssessments: {
      day: string;
      fullDate: string;
      meetingsThisDay: number;
      candidates: { name: string; hour: string; status: string; id: string }[];
    }[] = [];
    const yesterday = self.yesterday;
    const pastProjectDeadline = self.getDateWithRequiredFormat(self.projectDeadline);

    self.events = [
      { title: 'Past Dates', start: '1970-01-01', end: yesterday, repeat: 'never', categoryId: 2 },
      {
        title: 'Dates Past Deadline',
        start: pastProjectDeadline,
        end: '2999-12-31',
        repeat: 'never',
        categoryId: 6,
      },
    ];

    if (self.pureCalendar && !self.isInModal) self.events.pop();

    assessmentsArray.forEach((assessment: takenSlot | any) => {
      assessment.assessment_dates
        .sort((a, b) => (a.status < b.status ? -1 : 1))
        .every((assessmentDate: { date_start: string | number; status: string }) => {
          const continueLoop = true;

          const date = getYYYYMMDDDate(assessmentDate.date_start);
          const dayFoundInGroupedAssessments = groupedAssessments.find((gAssessment) => {
            return (
              gAssessment.fullDate === assessmentDate.date_start &&
              gAssessment.candidates[0].id !== assessment.candidate.id
            );
          });

          const objToPush = {
            day: date,
            fullDate: assessmentDate.date_start as string,
            meetingsThisDay: 1,
            candidates: [
              {
                name: `${assessment.candidate.first_name} ${assessment.candidate.surname}`,
                hour: hoursOfAssessment(assessment),
                status: assessmentDate.status,
                id: assessment.candidate.id,
              },
            ],
          };

          if (assessmentDate.status === 'confirmed') {
            groupedAssessments.push(objToPush);
            return !continueLoop;
          }

          if (dayFoundInGroupedAssessments) {
            const candidate = {
              name: `${assessment.candidate.first_name} ${assessment.candidate.surname}`,
              hour: hoursOfAssessment(assessment),
              status: assessmentDate.status,
              id: assessment.candidate.id,
            };

            dayFoundInGroupedAssessments.candidates.push(candidate);
            dayFoundInGroupedAssessments.candidates.sort(
              (a: { hour: string }, b: { hour: string }) =>
                +a.hour.split(':')[0] - +b.hour.split(':')[0],
            );
            dayFoundInGroupedAssessments.meetingsThisDay++;
          } else {
            groupedAssessments.push(objToPush);
          }

          return continueLoop;
        });
    });

    self.groupedAssessments = groupedAssessments.map((assessment) => {
      const dateHasBeenSelectedByTheUser =
        self.selectedDates.includes(assessment.day) ||
        (self.selectedSingleDate === assessment.day && self.pickSingleDate);

      return {
        title: 'Assessment',
        start: assessment.day,
        end: assessment.day,
        repeat: 'never',
        categoryId: dateHasBeenSelectedByTheUser
          ? 5
          : this.getAssessmentCategory(assessment.candidates),
        meetingsThisDay: assessment.meetingsThisDay,
        dayPart: assessment.day.split('-')[2],
        candidates: assessment.candidates,
      };
    });

    self.events.push(...self.groupedAssessments);

    const callbackToPass = (day: unknown) => (busyDay: { dayPart: string }) =>
      busyDay.dayPart === day;

    this.appendEventNumbersToDaysInCalendar(
      self,
      self.groupedAssessments,
      'calendar__meetings-count',
      'numOfAssessmentsThisDay',
      callbackToPass,
    );
  },

  applyDeadlinesToCalendar(self: Record<string, any>, eventsToDisplay: unknown): void {
    // AR === Assessment Request
    const groupedEvents: unknown[] = [];

    self.events = self.events.filter((event: { title: string }) => {
      return (
        event.title !== 'Training Event Deadline' &&
        event.title !== 'Project Deadline' &&
        event.title !== 'Assessment and Deadline' &&
        event.title !== 'Proposed Assessment and Deadline'
      );
    });

    // Step 1
    if (self.userRole !== 'candidate') {
      this.prepareGroupedEvents(eventsToDisplay, groupedEvents);
    } else {
      this.prepareGroupedTrainingEvents(eventsToDisplay, groupedEvents);
    }

    self.groupedDeadlines = [];
    self.daysWithVariousEvents = [];

    // Step 2
    if (self.userRole !== 'candidate') {
      this.prepareDeadlines(groupedEvents, self);
    } else {
      this.prepareTrainingDeadlines(groupedEvents, self);
    }

    self.events.push(...self.groupedDeadlines);

    if (self.daysWithVariousEvents.length) {
      self.events.push(...self.daysWithVariousEvents);
    }

    const callbackToPass = (day: any) => {
      const closure = (deadline: { start: string; dayPart: any }) => {
        const { dateFrom, dateTo } = self.getDateRangeForCurrentMonth();
        const deadlineIsInCurrentMonth =
          Date.parse(deadline.start) >= Date.parse(dateFrom) &&
          Date.parse(deadline.start) <= Date.parse(dateTo);

        if (deadlineIsInCurrentMonth) {
          return deadline.dayPart === day;
        }
      };

      return closure;
    };

    if (self.groupedDeadlines) {
      this.appendEventNumbersToDaysInCalendar(
        self,
        self.groupedDeadlines,
        'calendar__deadlines-count',
        'deadlinesThisDay',
        callbackToPass,
      );
    }

    if (self.daysWithVariousEvents) {
      this.appendEventNumbersToDaysInCalendar(
        self,
        self.daysWithVariousEvents,
        'calendar__various-events-count',
        'eventsThisDay',
        callbackToPass,
      );
    }
  },

  appendEventNumbersToDaysInCalendar(
    self: Record<string, any>,
    arrayToTraverse: any[],
    classToAdd: string,
    title: string,
    callback: (arg0: string) => any,
  ): void {
    if (!self.calendarExists) return;

    const allDaysElements = document.querySelectorAll('#sweetCalendar .day');

    allDaysElements.forEach((dayEl: HTMLElement) => {
      const daySpan = dayEl.querySelector('span').innerText;
      const day = daySpan.length < 2 ? `0${daySpan}` : daySpan;
      const correspondingArrayObj = arrayToTraverse.find(callback(day));

      if (correspondingArrayObj) {
        const tooltipThatAlreadyExistsForThisDay = dayEl.querySelector('.day__hover-tooltip');
        const eventNumberEl = document.createElement('div');
        const eventNumberClass = this.atLeastOneProposed(correspondingArrayObj)
          ? 'calendar__proposed-meetings-count'
          : classToAdd;

        eventNumberEl.classList.add(eventNumberClass);

        if (title === 'numOfAssessmentsThisDay') {
          if (
            dayEl.style.backgroundColor !== 'rgb(43, 195, 88)' &&
            correspondingArrayObj.categoryId === 8
          ) {
            dayEl.style.background = '-webkit-linear-gradient(0deg, #41bbdc 50%, #2b89c3 50%)';
            dayEl.style.color = '#fff';
            dayEl.style.fontWeight = 'bold';
          }

          eventNumberEl.innerText = correspondingArrayObj.meetingsThisDay;
        } else if (title === 'deadlinesThisDay') {
          eventNumberEl.innerText = correspondingArrayObj.deadlinesThisDay;
        } else if (title === 'eventsThisDay') {
          if (dayEl.style.backgroundColor !== 'rgb(43, 195, 88)') {
            dayEl.style.background = `-webkit-linear-gradient(
              0deg, #707070 50%,
              ${this.atLeastOneProposed(correspondingArrayObj) ? '#41bbdc' : '#2b89c3'} 50%)
              `;
            dayEl.style.color = '#fff';
            dayEl.style.fontWeight = 'bold';
          }

          dayEl.removeChild(dayEl.querySelector(`.${eventNumberClass}`));
          eventNumberEl.innerText = correspondingArrayObj.deadlinesThisDay;
        }

        const eventNumber = eventNumberEl.innerText;
        const formattedTitle = `${title}${this.getTitleFormat(correspondingArrayObj)}`;

        dayEl.appendChild(eventNumberEl);
        dayEl.setAttribute('title', `${self.$t(formattedTitle)} ${eventNumber}`);
        dayEl.setAttribute('data-is-busy', 'true');
        (dayEl.firstChild as HTMLElement).style.color = '#fff';

        let tooltip;

        if (!tooltipThatAlreadyExistsForThisDay) {
          tooltip = document.createElement('div');
          dayEl.appendChild(tooltip);
          tooltip.classList.add('day__hover-tooltip');
        }

        const adequateTooltip: HTMLElement = tooltipThatAlreadyExistsForThisDay
          ? tooltipThatAlreadyExistsForThisDay
          : tooltip;
        const tooltipData =
          title === 'numOfAssessmentsThisDay'
            ? correspondingArrayObj.candidates
            : correspondingArrayObj.clients;
        const tooltipTrainingData = correspondingArrayObj.trainingEvents;

        if (self.userRole === 'candidate') {
          const tooltipElement = document.querySelector('.day__hover-tooltip') as HTMLElement;
          tooltipElement.classList.add('tooltip-adjusted');

          tooltipTrainingData.forEach((obj: { hour: any; name: any; status: any }) => {
            const tooltipParagraph = document.createElement('p');
            tooltipParagraph.classList.add('tooltip-paragraph');
            const prefixedName = ['training', 'pre_work', 'post_work'].includes(obj.name)
              ? `Task: ${self.$t(this.parseTrainingData(obj.name))}`
              : `Session: ${obj.name}`;
            const text = document.createTextNode(
              `${obj.hour} ${prefixedName}  ${self.$t(this.parseTrainingStage(obj.status))}`,
            );
            tooltipParagraph.appendChild(text);
            adequateTooltip.append(tooltipParagraph);
          });
        } else {
          tooltipData.forEach((obj: { hour: any; name: any }) => {
            const tooltipParagraph = document.createElement('p');
            const text = document.createTextNode(`${obj.hour} ${obj.name}`);

            tooltipParagraph.appendChild(text);
            adequateTooltip.append(tooltipParagraph);
          });
        }

        adequateTooltip.style.backgroundColor =
          title === 'deadlinesThisDay'
            ? '#707070'
            : `${this.atLeastOneProposed(correspondingArrayObj) ? '#41bbdc' : '#2b89c3'}`;

        if (correspondingArrayObj.assessmentId && parseInt(eventNumber) === 1) {
          dayEl.setAttribute(
            'project',
            `${correspondingArrayObj.assessmentId}:${correspondingArrayObj.assessmentStatus}`,
          );
        }
      }
    });
  },

  parseTrainingStage(taskData: string): string {
    const statusMap = {
      pre_work: 'pre-work',
      post_work: 'post-work',
      training: 'training',
      new: 'new',
      completed: 'completed',
      missed: 'missed',
      rejected: 'rejected',
      accepted: 'completed',
      results_uploaded: 'submitted',
      in_progress: 'inProgress',
      cancelled: 'cancelled',
    };
    return statusMap[taskData] !== undefined ? statusMap[taskData] : taskData;
  },

  parseTrainingData(taskData: string): string {
    const statusMap = {
      pre_work: 'pre-work',
      post_work: 'post-work',
      training: 'training',
    };
    return statusMap[taskData] !== undefined ? statusMap[taskData] : taskData;
  },

  applyPreferredWorkingDaysToCalendar(preferredWorkingDays: string | any[]): void {
    if (!preferredWorkingDays) return;

    const calendarDays: Element[] = [];

    document.querySelectorAll('.day-name').forEach((dayEl) => calendarDays.push(dayEl));

    calendarDays.forEach((dayEl: HTMLElement) => {
      if (!preferredWorkingDays.includes(dayEl.title.toLowerCase())) dayEl.style.color = '#e4ece7';
    });
  },

  getAssessmentCategory(candidates: any[]): number {
    const allProposed = candidates.every((candidate: { status: string | string[] }) =>
      candidate.status.includes('proposed'),
    );
    const someProposed = candidates.some((candidate: { status: string | string[] }) =>
      candidate.status.includes('proposed'),
    );

    if (!allProposed && !someProposed) return 1;
    if (allProposed) return 7;
    if (someProposed) return 8;
  },

  getMixedDayTitle(day: any[]): string {
    return day.some((event: { categoryId: number }) => event.categoryId === 7)
      ? 'Proposed Assessment and Deadline'
      : 'Assessment and Deadline';
  },

  getMixedDayCategory(day: any[]): number {
    return day.some((event: { categoryId: number }) => event.categoryId === 7) ? 9 : 4;
  },

  atLeastOneProposed(assessmentObj: { categoryId: number }): boolean {
    const proposedAssessmentRelCategories = [7, 9];

    return proposedAssessmentRelCategories.some((evtCat) => assessmentObj.categoryId === evtCat);
  },

  getTitleFormat(assessmentObj: { categoryId: any }): string {
    // Look for eventCategories in Calendar.vue
    switch (assessmentObj.categoryId) {
      case 1:
        return '1';
      case 4:
        return '1';
      case 7:
        return '2';
      case 8:
        return '3';
      case 9:
        return '2';
      default:
        return '';
    }
  },

  prepareGroupedEvents(
    eventsToDisplay: any[],
    groupedEvents: {
      deadline: string;
      deadlinesThisDay: number;
      id: string;
      status: string;
      clients: { name: string; hour: string; id: string; status: string }[];
    }[],
  ): void {
    eventsToDisplay.forEach(
      (EV: {
        deadline: string | number;
        id: string;
        status: string;
        client: { company_name: string };
        name: string;
        type?: string;
      }) => {
        const date = getYYYYMMDDDate(EV.deadline);
        const deadlineFoundInGroupedEvents = groupedEvents.find(
          (groupedEvent: { deadline: string }) => groupedEvent.deadline === date,
        );

        const objToPush = {
          deadline: date,
          deadlinesThisDay: 1,
          id: EV.id,
          status: EV.status,
          clients: [
            {
              name:
                this.calRef.userRole === 'assessor'
                  ? `${EV.client.company_name} - ${EV.name}`
                  : EV.name,
              hour: getMeetingDateAndTime(EV.deadline).split(' ')[1] || '',
              id: EV.id,
              status: EV.status,
            },
          ],
        };

        if (deadlineFoundInGroupedEvents) {
          const client = {
            name:
              this.calRef.userRole === 'assessor'
                ? `${EV.client.company_name} - ${EV.name}`
                : EV.name,
            hour: getMeetingDateAndTime(EV.deadline).split(' ')[1] || '',
            id: EV.id,
            status: EV.status,
          };

          deadlineFoundInGroupedEvents.clients.push(client);
          deadlineFoundInGroupedEvents.deadlinesThisDay++;
        } else groupedEvents.push(objToPush);
      },
    );
  },
  prepareGroupedTrainingEvents(
    eventsToDisplay: any[],
    groupedEvents: {
      deadline: string;
      deadlinesThisDay: number;
      id: string;
      status: string;
      name?: string;
      type?: string;
      trainingEvents: { name: string; hour: string; id: string; status: string; type: string }[];
    }[],
  ): void {
    eventsToDisplay.forEach(
      (EV: {
        deadline: string | number;
        id: string;
        status: string;
        name: string;
        type?: string;
      }) => {
        const date = getYYYYMMDDDate(EV.deadline);
        const deadlineFoundInGroupedEvents = groupedEvents.find(
          (groupedEvent: { deadline: string }) => groupedEvent.deadline === date,
        );

        const trainingEvent = {
          name: EV.name,
          hour: getMeetingDateAndTime(EV.deadline).split(' ')[1] || '',
          id: EV.id,
          status: EV.status,
          type: EV.type || '',
        };

        if (deadlineFoundInGroupedEvents) {
          deadlineFoundInGroupedEvents.trainingEvents.push(trainingEvent);
          deadlineFoundInGroupedEvents.deadlinesThisDay++;
        } else {
          const objToPush = {
            deadline: date,
            deadlinesThisDay: 1,
            id: EV.id,
            status: EV.status,
            type: EV.type || '',
            name: EV.name,
            trainingEvents: [trainingEvent],
          };
          groupedEvents.push(objToPush);
        }
      },
    );
  },

  prepareDeadlines(groupedEvents: any[], self: Record<string, any>): void {
    groupedEvents.forEach(
      (EV: {
        deadline: string;
        deadlinesThisDay: any;
        id: string;
        status: string;
        clients: any;
      }) => {
        const mixedDay = self.events.filter((event: { title: string; dayPart: string }) => {
          return (
            (event.title === 'Assessment' || event.title === 'Proposed Assessment') &&
            event.dayPart === EV.deadline.split('-')[2]
          );
        });

        const deadline = {
          title: mixedDay.length ? this.getMixedDayTitle(mixedDay) : 'Project Deadline',
          start: EV.deadline,
          end: EV.deadline,
          repeat: 'never',
          categoryId: mixedDay.length ? this.getMixedDayCategory(mixedDay) : 3,
          deadlinesThisDay: mixedDay.length
            ? mixedDay[0].meetingsThisDay + EV.deadlinesThisDay
            : EV.deadlinesThisDay,
          dayPart: EV.deadline.split('-')[2],
          assessmentId: EV.id,
          assessmentStatus: EV.status,
          clients: EV.clients,
        };

        const dateWithDeadlineSelectedEarlier =
          self.selectedSingleDate === deadline.start && self.pickSingleDate;

        if (!deadline.assessmentId && !deadline.assessmentStatus) {
          delete deadline.assessmentId, deadline.assessmentStatus;
        }

        if (self.selectedDates.includes(deadline.start) || dateWithDeadlineSelectedEarlier) {
          deadline.categoryId = 5;
        }

        deadline.title === 'Project Deadline'
          ? self.groupedDeadlines.push(deadline)
          : self.daysWithVariousEvents.push(deadline);
      },
    );
  },
  prepareTrainingDeadlines(groupedEvents: any[], self: Record<string, any>): void {
    groupedEvents.forEach(
      (EV: {
        deadline: string;
        deadlinesThisDay: any;
        id: string;
        name?: string;
        status: string;
        type?: string;
        trainingEvents: any;
      }) => {
        const mixedDay = self.events.filter((event: { title: string; dayPart: string }) => {
          return event.dayPart === EV.deadline.split('-')[2];
        });

        const deadline = {
          title: mixedDay.length ? this.getMixedDayTitle(mixedDay) : 'Training Event Deadline',
          start: EV.deadline,
          end: EV.deadline,
          repeat: 'never',
          categoryId: mixedDay.length ? this.getMixedDayCategory(mixedDay) : 10,
          deadlinesThisDay: mixedDay.length
            ? mixedDay[0].meetingsThisDay + EV.deadlinesThisDay
            : EV.deadlinesThisDay,
          dayPart: EV.deadline.split('-')[2],
          trainingEvents: EV.trainingEvents,
        };

        deadline.title === 'Training Event Deadline'
          ? self.groupedDeadlines.push(deadline)
          : self.daysWithVariousEvents.push(deadline);
      },
    );
  },
};

export default applyData;
